import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bold, Italic, UnderlineIcon, List, ListOrdered, Link2, Image as ImageIcon, Youtube as YoutubeIcon, AlignLeft, AlignCenter, Highlighter, Save, Eye, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Prévention & Droits', 'Santé & Bien-être', 'Famille & Éducation',
  'Compétences pro', 'Culture & Société', 'Entreprise'];
const TYPES = [
  { value: 'editorial', label: '📰 Éditorial' },
  { value: 'invite', label: '👤 Invité' },
  { value: 'etude', label: '📊 Étude & Chiffres' },
  { value: 'prolongement', label: '🔗 Cours lié' },
];

function ToolbarButton({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded-lg transition-colors ${active ? 'bg-[#ff6b6b]/20 text-[#ff6b6b]' : 'text-[#a0a0ab] hover:text-white hover:bg-white/10'}`}
    >
      {children}
    </button>
  );
}

export default function ArticleEditor() {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const isEdit = Boolean(articleId);

  const [form, setForm] = useState({
    title: '', slug: '', cover_image: '', category: CATEGORIES[0],
    article_type: 'editorial', tags: '', meta_description: '',
    author_bio: '', linked_course_id: '', status: 'draft', is_featured: false
  });
  const [pexelsResults, setPexelsResults] = useState([]);
  const [pexelsQuery, setPexelsQuery] = useState('');
  const [showPexels, setShowPexels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Image.configure({ inline: false, allowBase64: false }),
      Youtube.configure({ controls: true, width: 640, height: 360 }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#ff6b6b] underline' } }),
      Highlight.configure({ multicolor: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Commencez à rédiger votre article...' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none focus:outline-none min-h-[300px] text-[#e8e8e8] leading-relaxed',
      },
    },
  });

  // Load article if editing
  useEffect(() => {
    if (isEdit && editor) {
      axios.get(`${API}/api/articles/admin/all`, { withCredentials: true })
        .then(r => {
          const a = r.data.articles.find(x => x.article_id === articleId);
          if (a) {
            setForm({ ...a, tags: (a.tags || []).join(', '), linked_course_id: a.linked_course_id || '' });
            // Load full content
            return axios.get(`${API}/api/articles/${a.slug}`, { withCredentials: true });
          }
        })
        .then(r => {
          if (r?.data?.content) editor.commands.setContent(r.data.content);
        })
        .catch(() => toast.error('Erreur chargement'))
        .finally(() => setLoading(false));
    }
  }, [isEdit, editor, articleId]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEdit && form.title) {
      const slug = form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setForm(f => ({ ...f, slug }));
    }
  }, [form.title, isEdit]);

  const searchPexels = async () => {
    if (!pexelsQuery) return;
    try {
      const r = await axios.get(`${API}/api/articles/pexels/search`, {
        params: { q: pexelsQuery }, withCredentials: true
      });
      setPexelsResults(r.data.photos);
    } catch { toast.error('Erreur Pexels'); }
  };

  const addImage = () => {
    const url = prompt('URL de l\'image :');
    if (url) editor?.chain().focus().setImage({ src: url }).run();
  };

  const addYoutube = () => {
    const url = prompt('URL YouTube :');
    if (url) editor?.chain().focus().setYoutubeVideo({ src: url }).run();
  };

  const addLink = () => {
    const url = prompt('URL du lien :');
    if (url) editor?.chain().focus().setLink({ href: url }).run();
  };

  const handleSave = async (status = form.status) => {
    if (!form.title || !form.category) { toast.error('Titre et catégorie requis'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        content: editor?.getHTML() || '',
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        status,
        linked_course_id: form.linked_course_id || null,
      };
      if (isEdit) {
        await axios.put(`${API}/api/articles/${articleId}`, payload, { withCredentials: true });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/api/articles`, payload, { withCredentials: true });
        toast.success(status === 'published' ? 'Article publié !' : 'Brouillon sauvegardé');
      }
      navigate('/superadmin?tab=articles');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0f0e17] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0f0e17]">
      <header className="sticky top-0 z-50 bg-[#0f0e17]/90 backdrop-blur-xl border-b border-white/5 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/superadmin?tab=articles')} className="flex items-center gap-2 text-[#e8e8e8] hover:text-white">
            <ArrowLeft size={18} /> Super Admin
          </button>
          <div className="flex gap-2">
            <button onClick={() => handleSave('draft')} disabled={saving} className="flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white text-sm px-3 py-1.5 rounded-xl transition-colors border border-white/10 disabled:opacity-50">
              <Save size={14} /> Brouillon
            </button>
            <button onClick={() => handleSave('published')} disabled={saving} className="btn-primary text-sm py-1.5 px-4 flex items-center gap-1.5 disabled:opacity-50">
              <Send size={14} /> {saving ? '...' : 'Publier'}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-heading font-bold text-white">{isEdit ? 'Modifier l\'article' : 'Nouvel article'}</h1>

        {/* Title */}
        <div>
          <label className="text-xs text-[#a0a0ab] mb-1.5 block">Titre *</label>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Titre de l'article"
            className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-base font-medium focus:outline-none focus:border-[#ff6b6b]/50"
            data-testid="article-title-input"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="text-xs text-[#a0a0ab] mb-1.5 block">Slug (URL)</label>
          <input
            type="text"
            value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
            className="w-full bg-white/5 border border-white/10 text-[#e8e8e8] rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-[#ff6b6b]/50"
          />
          <p className="text-xs text-[#a0a0ab] mt-1">/articles/{form.slug}</p>
        </div>

        {/* Cover image */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs text-[#a0a0ab]">Image de couverture (URL)</label>
            <button type="button" onClick={() => setShowPexels(!showPexels)} className="text-xs text-[#ff6b6b] hover:underline flex items-center gap-1">
              <ImageIcon size={11} /> Recherche Pexels
            </button>
          </div>
          <input
            type="url"
            value={form.cover_image}
            onChange={e => setForm(f => ({ ...f, cover_image: e.target.value }))}
            placeholder="https://images.pexels.com/..."
            className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
          />
          {form.cover_image && <img src={form.cover_image} alt="" className="mt-2 rounded-xl h-32 w-full object-cover" />}
          {showPexels && (
            <div className="mt-2 bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={pexelsQuery}
                  onChange={e => setPexelsQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchPexels()}
                  placeholder="Rechercher sur Pexels..."
                  className="flex-1 bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-lg px-3 py-2 text-xs focus:outline-none"
                />
                <button type="button" onClick={searchPexels} className="btn-primary text-xs py-2 px-3">Chercher</button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {pexelsResults.map(p => (
                  <img key={p.id} src={p.thumb} alt={p.photographer} className="rounded-lg h-20 w-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => { setForm(f => ({ ...f, cover_image: p.url })); setShowPexels(false); }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Category, Type, Tags */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">Catégorie *</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">Type</label>
            <select value={form.article_type} onChange={e => setForm(f => ({ ...f, article_type: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none">
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">Tags (séparés par virgules)</label>
            <input type="text" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="rgpd, santé, travail..."
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1.5 block">ID du cours lié (optionnel)</label>
            <input type="text" value={form.linked_course_id} onChange={e => setForm(f => ({ ...f, linked_course_id: e.target.value }))}
              placeholder="course_id..."
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
          </div>
        </div>

        <div>
          <label className="text-xs text-[#a0a0ab] mb-1.5 block">Meta description SEO (max 160 car.)</label>
          <textarea value={form.meta_description} onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))}
            maxLength={160} rows={2}
            className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none"
            placeholder="Description courte pour les moteurs de recherche..." />
        </div>

        <div>
          <label className="text-xs text-[#a0a0ab] mb-1.5 block">Bio auteur</label>
          <input type="text" value={form.author_bio} onChange={e => setForm(f => ({ ...f, author_bio: e.target.value }))}
            className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2.5 text-sm focus:outline-none"
            placeholder="Rédacteur Kurso, expert en..." />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="rounded" />
          <span className="text-sm text-[#e8e8e8]">Épingler comme article à la une</span>
        </label>

        {/* WYSIWYG Editor */}
        <div>
          <label className="text-xs text-[#a0a0ab] mb-2 block">Contenu de l'article *</label>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-1 bg-white/5 border border-white/10 rounded-t-xl px-3 py-2">
            <ToolbarButton onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} title="Gras">
              <Bold size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} title="Italique">
              <Italic size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} title="Souligné">
              <UnderlineIcon size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor?.chain().focus().toggleHighlight().run()} active={editor?.isActive('highlight')} title="Surligné">
              <Highlighter size={14} />
            </ToolbarButton>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <ToolbarButton onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive('heading', { level: 2 })} title="Titre H2">
              <span className="text-xs font-bold">H2</span>
            </ToolbarButton>
            <ToolbarButton onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} active={editor?.isActive('heading', { level: 3 })} title="Titre H3">
              <span className="text-xs font-bold">H3</span>
            </ToolbarButton>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <ToolbarButton onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} title="Liste à puces">
              <List size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')} title="Liste numérotée">
              <ListOrdered size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor?.chain().focus().toggleBlockquote().run()} active={editor?.isActive('blockquote')} title="Citation / Pull quote">
              <span className="text-xs font-bold">"</span>
            </ToolbarButton>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('left').run()} active={editor?.isActive({ textAlign: 'left' })} title="Aligner gauche">
              <AlignLeft size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('center').run()} active={editor?.isActive({ textAlign: 'center' })} title="Centrer">
              <AlignCenter size={14} />
            </ToolbarButton>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <ToolbarButton onClick={addLink} active={editor?.isActive('link')} title="Insérer un lien">
              <Link2 size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={addImage} title="Insérer une image">
              <ImageIcon size={14} />
            </ToolbarButton>
            <ToolbarButton onClick={addYoutube} title="Intégrer YouTube">
              <span className="text-xs">YT</span>
            </ToolbarButton>
          </div>
          {/* Editor area */}
          <div className="bg-white/5 border border-white/10 border-t-0 rounded-b-xl px-4 py-4 min-h-[300px]">
            <EditorContent editor={editor} data-testid="article-editor" />
          </div>
        </div>

        {/* Save buttons */}
        <div className="flex gap-3 pb-8">
          <button onClick={() => handleSave('draft')} disabled={saving} className="flex-1 bg-white/10 hover:bg-white/15 text-white py-3 rounded-xl font-medium transition-colors border border-white/10 disabled:opacity-50">
            <Save size={16} className="inline mr-2" /> Sauvegarder brouillon
          </button>
          <button onClick={() => handleSave('published')} disabled={saving} className="flex-1 btn-primary py-3 disabled:opacity-50">
            <Send size={16} className="inline mr-2" /> {saving ? 'Publication...' : 'Publier l\'article'}
          </button>
        </div>
      </div>
    </div>
  );
}
